/**
 * Locations page consumption:
 * const { state, isLoading } = useSitesResultList(sitesHeadlessEngine, {
 *   latitude, longitude, sortByDistance: true, communities,
 * });
 *
 * Omit distanceMiles for distance sort without a radius filter (qf + sort only).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SearchEngine,
  buildResultList,
  ResultListState,
  ResultList,
  loadQueryActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
  loadAdvancedSearchQueryActions,
  buildResultsPerPage,
  Sort,
} from '@coveo/headless';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { SITE_FIELDS_TO_INCLUDE } from 'lib/types/coveo/SiteSearchResult';
import { buildSitesAdvancedQuery } from 'lib/utils/coveo/sitesHeadless/sitesAdvancedQuery';
import {
  buildSitesHeadlessSort,
  sitesRelevanceSortCriterion,
} from 'lib/utils/coveo/sitesHeadless/sitesHeadlessSort';

export interface UseSitesSearchOptions {
  query?: string;
  latitude?: number;
  longitude?: number;
  distanceMiles?: number;
  sortByDistance?: boolean;
  communities?: string[];
  resultsPerPage?: number;
}

const DEFAULT_RESULTS_PER_PAGE = 100000;
export const useSitesResultList = (
  // eslint-disable-next-line @typescript-eslint/ban-types
  engine: SearchEngine<{}>,
  options: UseSitesSearchOptions = {}
) => {
  const {
    query,
    latitude,
    longitude,
    distanceMiles,
    sortByDistance = latitude != null && longitude != null,
    communities,
    resultsPerPage = DEFAULT_RESULTS_PER_PAGE,
  } = options;

  const [state, setState] = useState<ResultListState>();
  const resultListRef = useRef<ResultList | null>(null);
  const sortControllerRef = useRef<Sort | null>(null);
  const resultsPerPageControllerRef = useRef<ReturnType<typeof buildResultsPerPage> | null>(null);
  const { sitecoreContext } = useSitecoreContext();

  const runSearch = useCallback(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    const { updateQuery } = loadQueryActions(engine);
    const searchQueryActionCreators = loadAdvancedSearchQueryActions(engine);
    const { executeSearch } = loadSearchActions(engine);
    const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);

    if (!sortControllerRef.current) {
      sortControllerRef.current = buildSitesHeadlessSort(engine).sortController;
    }
    const sortController = sortControllerRef.current;

    const aq = buildSitesAdvancedQuery({
      latitude,
      longitude,
      distanceMiles,
      sortByDistance,
      communities,
    });

    engine.dispatch(
      searchQueryActionCreators.updateAdvancedSearchQueries({
        aq,
      })
    );

    if (query) {
      engine.dispatch(updateQuery({ q: query }));
    } else {
      engine.dispatch(updateQuery({ q: '' }));
    }

    // Relevance sort so AQ $sort on @distance is not overridden by a field sort.
    sortController.sortBy(sitesRelevanceSortCriterion);
    engine.dispatch(executeSearch(logInterfaceLoad()));
  }, [
    engine,
    query,
    latitude,
    longitude,
    distanceMiles,
    sortByDistance,
    communities,
    sitecoreContext?.pageState,
  ]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    const resultList: ResultList = buildResultList(engine, {
      options: {
        fieldsToInclude: SITE_FIELDS_TO_INCLUDE,
      },
    });

    resultListRef.current = resultList;
    setState(resultList.state);

    const unsubscribe = resultList.subscribe(() => {
      setState(resultList.state);
    });

    return () => {
      unsubscribe();
    };
  }, [engine, sitecoreContext?.pageState]);

  useEffect(() => {
    resultsPerPageControllerRef.current = null;
  }, [engine]);

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    if (!resultsPerPageControllerRef.current) {
      resultsPerPageControllerRef.current = buildResultsPerPage(engine, {
        initialState: { numberOfResults: resultsPerPage },
      });
    } else {
      resultsPerPageControllerRef.current.set(resultsPerPage);
    }
  }, [engine, resultsPerPage, sitecoreContext?.pageState]);

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  return {
    state,
    isLoading: state?.isLoading ?? true,
    executeSearch: runSearch,
  };
};

export default useSitesResultList;
